import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Minus, Leaf, Clock, Bike, MapPin, ShoppingBag, ArrowRight, Star, Info, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { LoadingBlock, EmptyState } from "../../components/app/ui";
import { useCart } from "../../context/CartContext";
import { foodApi, addressApi, inr } from "../../lib/api";
import RideMap from "../../components/app/RideMap";
import { Navigation } from "lucide-react";
import { IMAGES } from "../../lib/constants";

const WEEK = [
  { key: "mon", label: "Monday" }, { key: "tue", label: "Tuesday" }, { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" }, { key: "fri", label: "Friday" }, { key: "sat", label: "Saturday" }, { key: "sun", label: "Sunday" },
];


export default function RestaurantDetail() {
  const { restaurantId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [vegOnly, setVegOnly] = useState(false);
  const [category, setCategory] = useState("all");
  const [showProfile, setShowProfile] = useState(false);
  const { cart, addItem, updateQty, subtotal, count } = useCart();

  useEffect(() => {
    addressApi.list().then((list) => {
      const def = list.find((a) => a.is_default) || list[0];
      const params = def && Number.isFinite(def.lat) ? { lat: def.lat, lng: def.lng } : undefined;
      foodApi.restaurant(restaurantId, params).then(setData).catch(() => setError("Restaurant not found"));
    }).catch(() => foodApi.restaurant(restaurantId).then(setData).catch(() => setError("Restaurant not found")));
  }, [restaurantId]);

  const restaurant = data?.restaurant;
  const menu = useMemo(() => data?.menu || [], [data]);
  const categories = useMemo(() => ["all", ...Array.from(new Set(menu.map((m) => m.category || "Mains")))], [menu]);
  const visible = menu.filter((m) => (!vegOnly || m.veg) && (category === "all" || (m.category || "Mains") === category));
  const grouped = useMemo(() => visible.reduce((acc, m) => { const k = m.category || "Mains"; (acc[k] = acc[k] || []).push(m); return acc; }, {}), [visible]);
  const qtyOf = (id) => (cart.restaurant?.restaurant_id === restaurantId ? cart.items.find((i) => i.item_id === id)?.qty || 0 : 0);
  const cartIsHere = cart.restaurant?.restaurant_id === restaurantId && count > 0;

  const add = (item) => {
    if (restaurant.out_of_range) { toast.error("This restaurant doesn't deliver to your location."); return; }
    if (cart.restaurant && cart.restaurant.restaurant_id !== restaurantId && cart.items.length) toast.info(`Started a new cart for ${restaurant.name}`);
    addItem(restaurant, item);
  };

  if (error) return <EmptyState title={error} action={{ to: "/app/food", label: "Back to restaurants" }} />;
  if (!restaurant) return <LoadingBlock rows={4} />;

  return (
    <>
      <Link to="/app/food" className="inline-flex items-center text-sm font-medium text-ink-soft hover:text-brand" data-testid="back-to-food"><ArrowLeft className="mr-1.5 h-4 w-4" /> All restaurants</Link>
      <div className="kt-card mt-4 overflow-hidden">
        <div className="relative h-52 sm:h-64">
          {restaurant.image ? <img src={restaurant.image} alt={restaurant.name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-surface-200" data-testid="restaurant-cover-blank" />}
          <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-6 text-surface">
            <span className="rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-ink">{restaurant.cuisine}</span>
            <h1 className="mt-2 text-3xl font-bold" data-testid="restaurant-name">{restaurant.name}</h1>
            {restaurant.description && <p className="mt-1 max-w-2xl text-sm text-surface/85">{restaurant.description}</p>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-5 px-6 py-4 text-sm text-ink-soft">
          {restaurant.rating ? <span className="inline-flex items-center gap-1.5 font-semibold text-amber-700"><Star className="h-4 w-4 fill-amber-500 text-amber-500" />{restaurant.rating} <span className="font-normal text-ink-muted">({restaurant.rating_count})</span></span> : <span className="inline-flex items-center gap-1.5 text-ink-muted"><Star className="h-4 w-4" />New</span>}
          <span className="inline-flex items-center gap-1.5"><Bike className="h-4 w-4" />{inr(restaurant.delivery_fee)} delivery</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="h-4 w-4" />Min order {inr(restaurant.min_order)}</span>
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${restaurant.accepting_orders ? "bg-graphite-50 text-graphite" : restaurant.open_now ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`} data-testid="detail-status-badge">{restaurant.accepting_orders ? "Open now" : restaurant.open_now ? "Orders paused" : "Closed"}</span>
          <button onClick={() => setShowProfile(true)} className="ml-auto inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold text-brand ring-1 ring-brand/30 transition-colors hover:bg-brand-50" data-testid="open-restaurant-profile"><Info className="h-3.5 w-3.5" /> Restaurant info</button>
        </div>
        {restaurant.out_of_range && (
          <div className="mx-6 mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="too-far-banner">
            <p className="font-semibold">Too far to deliver</p>
            <p className="mt-0.5">You're {restaurant.distance_km} km away, outside this restaurant's delivery area. Ordering is disabled for your saved location.</p>
          </div>
        )}
        {!restaurant.accepting_orders && (
          <div className="mx-6 mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="closed-banner">
            <p className="font-semibold">{restaurant.paused ? "Orders are paused right now." : "This restaurant is currently closed."}</p>
            <p className="mt-0.5" data-testid="reopen-message">{restaurant.closed_message || "Please check back later."}</p>
          </div>
        )}
      </div>

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
        <div>
          <div className="mb-6 flex flex-wrap items-center gap-2">
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)} className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors ${category === c ? "bg-brand text-white" : "bg-white text-ink-soft ring-1 ring-surface-300 hover:text-ink"}`} data-testid={`menu-cat-${c}`}>{c === "all" ? "All items" : c}</button>
            ))}
            <button onClick={() => setVegOnly((v) => !v)} className={`ml-auto inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${vegOnly ? "bg-graphite text-white" : "bg-white text-ink-soft ring-1 ring-surface-300"}`} data-testid="veg-toggle"><Leaf className="h-4 w-4" /> Veg only</button>
          </div>
          {Object.keys(grouped).length === 0 && <EmptyState title="No items match" text="Try clearing the filters." />}
          <div className="space-y-10">
            {Object.entries(grouped).map(([cat, items]) => (
              <section key={cat}>
                <h2 className="mb-4 text-lg font-semibold text-ink">{cat} <span className="text-sm font-normal text-ink-muted">({items.length})</span></h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {items.map((m) => {
                    const qty = qtyOf(m.item_id);
                    return (
                      <div key={m.item_id} className={`kt-card flex gap-4 p-4 ${!m.is_available ? "opacity-60" : ""}`} data-testid={`menu-item-${m.item_id}`}>
                        {m.image ? <img src={m.image} alt={m.name} className="h-24 w-24 shrink-0 rounded-xl object-cover" loading="lazy" /> : null}
                        <div className="flex min-w-0 flex-1 flex-col">
                          <div className="flex items-start gap-2">
                            <span className={`mt-1 h-3.5 w-3.5 shrink-0 rounded-sm border-2 ${m.veg ? "border-graphite" : "border-red-600"} flex items-center justify-center`}><span className={`h-1.5 w-1.5 rounded-full ${m.veg ? "bg-graphite" : "bg-red-600"}`} /></span>
                            <h3 className="text-sm font-semibold text-ink">{m.name}</h3>
                          </div>
                          {m.description && <p className="mt-1 line-clamp-2 text-xs text-ink-soft">{m.description}</p>}
                          <div className="mt-auto flex items-center justify-between pt-3">
                            <span className="text-sm font-bold text-ink">{inr(m.price)}</span>
                            {!m.is_available ? <span className="text-xs font-medium text-ink-muted">Unavailable</span> : qty === 0 ? (
                              <Button size="sm" onClick={() => add(m)} className="h-8 rounded-full bg-brand px-4 text-white hover:bg-brand-600" data-testid={`add-${m.item_id}`}><Plus className="mr-1 h-3.5 w-3.5" /> Add</Button>
                            ) : (
                              <div className="flex items-center gap-1 rounded-full bg-brand-50 p-0.5">
                                <button onClick={() => updateQty(m.item_id, qty - 1)} className="flex h-7 w-7 items-center justify-center rounded-full text-brand hover:bg-white" data-testid={`dec-${m.item_id}`}><Minus className="h-3.5 w-3.5" /></button>
                                <span className="w-6 text-center text-sm font-semibold text-ink" data-testid={`qty-${m.item_id}`}>{qty}</span>
                                <button onClick={() => add(m)} className="flex h-7 w-7 items-center justify-center rounded-full text-brand hover:bg-white" data-testid={`inc-${m.item_id}`}><Plus className="h-3.5 w-3.5" /></button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <div className="kt-card p-5" data-testid="cart-summary">
            <h3 className="flex items-center gap-2 text-base font-semibold text-ink"><ShoppingBag className="h-4 w-4 text-brand" /> Your cart</h3>
            {!cartIsHere ? (
              <p className="mt-3 text-sm text-ink-soft">Add items from the menu to start an order.</p>
            ) : (
              <>
                <ul className="mt-4 divide-y divide-surface-200 text-sm">
                  {cart.items.map((i) => (
                    <li key={i.item_id} className="flex items-center justify-between py-2.5">
                      <span className="text-ink">{i.qty} × {i.name}</span>
                      <span className="font-medium text-ink">{inr(i.price * i.qty)}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex items-center justify-between border-t border-surface-200 pt-3 text-sm">
                  <span className="text-ink-soft">Subtotal</span><span className="font-bold text-ink" data-testid="cart-subtotal">{inr(subtotal)}</span>
                </div>
                {subtotal < (restaurant.min_order || 0) && <p className="mt-2 inline-flex items-center gap-1 rounded-lg bg-coral-50 px-2 py-1 text-xs font-bold text-coral-600">Add {inr(restaurant.min_order - subtotal)} more to reach the minimum order.</p>}
                <Button asChild disabled={subtotal < (restaurant.min_order || 0)} className="mt-4 h-11 w-full rounded-full bg-brand text-base font-bold text-white hover:bg-brand-600">
                  <Link to="/app/checkout" data-testid="go-checkout">Checkout <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
              </>
            )}
          </div>
        </aside>
      </div>

      <Sheet open={showProfile} onOpenChange={setShowProfile}>
        <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-3xl" data-testid="restaurant-profile-sheet">
          <SheetHeader className="text-left">
            <SheetTitle className="text-xl" data-testid="profile-name">{restaurant.name}</SheetTitle>
            <p className="text-sm text-ink-muted">{restaurant.cuisine}{restaurant.description ? ` · ${restaurant.description}` : ""}</p>
          </SheetHeader>
          <div className="mt-4 space-y-6 pb-2">
            <div className="flex items-center gap-2 text-sm" data-testid="profile-rating">
              {restaurant.rating ? <span className="inline-flex items-center gap-1.5 font-semibold text-amber-700"><Star className="h-4 w-4 fill-amber-500 text-amber-500" />{restaurant.rating} <span className="font-normal text-ink-muted">({restaurant.rating_count} ratings)</span></span> : <span className="inline-flex items-center gap-1.5 text-ink-muted"><Star className="h-4 w-4" />New restaurant</span>}
              <span className={`ml-auto rounded-full px-2.5 py-1 text-xs font-semibold ${restaurant.accepting_orders ? "bg-graphite-50 text-graphite" : restaurant.open_now ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>{restaurant.accepting_orders ? "Open now" : restaurant.open_now ? "Orders paused" : "Closed"}</span>
            </div>

            <div data-testid="profile-contact">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Contact</h3>
              <div className="space-y-2 text-sm text-ink-soft">
                {restaurant.address && <p className="flex items-start gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{restaurant.address}</p>}
                {restaurant.contact_phone && <a href={`tel:${restaurant.contact_phone}`} className="flex items-center gap-2 font-medium text-ink hover:text-brand" data-testid="profile-phone"><Phone className="h-4 w-4 text-brand" />{restaurant.contact_phone}</a>}
                {restaurant.contact_email && <a href={`mailto:${restaurant.contact_email}`} className="flex items-center gap-2 hover:text-brand" data-testid="profile-email"><Mail className="h-4 w-4 text-brand" />{restaurant.contact_email}</a>}
              </div>
            </div>

            <div data-testid="profile-hours">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Business hours</h3>
              {restaurant.hours && Object.keys(restaurant.hours).length > 0 ? (
                <ul className="grid gap-1 text-sm sm:grid-cols-2">
                  {WEEK.map(({ key, label }) => {
                    const d = restaurant.hours[key];
                    const isToday = key === WEEK[(new Date().getDay() + 6) % 7].key;
                    return (
                      <li key={key} className={`flex items-center justify-between rounded-lg px-3 py-1.5 ${isToday ? "bg-brand-50 font-semibold text-ink" : "text-ink-soft"}`} data-testid={`hours-${key}`}>
                        <span>{label}{isToday ? " · Today" : ""}</span>
                        <span>{!d || d.closed ? "Closed" : `${d.open} – ${d.close}`}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-sm text-ink-muted" data-testid="hours-unset">Opening hours not published yet.</p>
              )}
            </div>

            {Number.isFinite(restaurant.lat) && Number.isFinite(restaurant.lng) && (
              <div data-testid="profile-location">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Directions</h3>
                  <a href={`https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-brand px-4 py-1.5 text-xs font-semibold text-white hover:bg-brand-600" data-testid="restaurant-directions"><Navigation className="h-3.5 w-3.5" /> Get directions</a>
                </div>
                <div className="overflow-hidden rounded-xl"><RideMap drop={{ lat: restaurant.lat, lng: restaurant.lng }} className="h-48" /></div>
              </div>
            )}

            {(restaurant.gallery || []).length > 0 && (
              <div data-testid="restaurant-gallery">
                <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-ink-muted">Gallery</h3>
                <div className="flex gap-3 overflow-x-auto">
                  {restaurant.gallery.map((g) => <img key={g} src={g} alt="Dish" className="h-24 w-36 shrink-0 rounded-xl object-cover" loading="lazy" />)}
                </div>
              </div>
            )}

            <Button onClick={() => setShowProfile(false)} className="h-11 w-full rounded-full bg-brand text-base font-bold text-white hover:bg-brand-600" data-testid="profile-view-menu">View menu &amp; order</Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
